import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_USE_TAGS_FROM_AI, FOXTROT_NEW_AI_CHAT_INTERFACE } from '@/config/GrowthBook/constants';
import {
  authActions, commonActions, merlinActions, onboardingActions, preChatActions,
} from '@/redux/modules';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';
import { INTERNAL_REDIRECT_OPTIONS } from '@/components/Layout/Menu/HeaderV2/AiChat/AiChat.enum';


const ChatbotContext = createContext({});

export const ChatbotProvider = ({ children }) => {
  const { jwt } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const history = useHistory();
  const [isActive, setIsActive] = useState(false);
  const [isChatbotReady, setIsChatbotReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const newAiChatInterface = useFeatureIsOn(FOXTROT_NEW_AI_CHAT_INTERFACE);
  const openZendeskFromAi = useFeatureIsOn(ATIVACAO_USE_TAGS_FROM_AI);
  const { routes: routesLocale } = useLocale();

  const on = useCallback((event, callback) => {
    if (!isActive) {
      return;
    }

    window.Chatbot.on(event, callback);
  }, [isActive]);

  const off = useCallback((event) => {
    if (!isActive) {
      return;
    }

    window.Chatbot.off(event);
  }, [isActive]);

  const handleStateChange = useCallback((state) => {
    Object.keys(state).forEach((key) => {
      const value = state[key];
      const actions = {
        fullScreen: () => dispatch(merlinActions.inhouse.toggleFullScreen(value)),
        currentChat: () => dispatch(merlinActions.inhouse.setCurrentChatState(value)),
        minimizedChat: () => dispatch(merlinActions.inhouse.minimizeInHousechat(value)),
        renderInHouseChat: () => dispatch(merlinActions.inhouse.renderInHouseChat(value)),
        loadingTranscription: () => dispatch(merlinActions.inhouse.setLoadingTranscription(value)),
        visibleAiChat: () => dispatch(merlinActions.inhouse.visibilityInHousechat(value)),
      };

      const action = actions[key];
      action && action();
    });
  }, [dispatch]);

  const handleOpenSupport = useCallback((params) => {
    dispatch(preChatActions.n8n.forceOpenSupport({
      bool: !openZendeskFromAi,
      n8nParams: params,
    }));
  }, [dispatch, openZendeskFromAi]);

  const handleMigrationRedirect = useCallback((params) => {
    if (!params) {
      return;
    }

    dispatch(
      merlinActions.inhouse.setMigrationData({
        hostingId: params.hosting_id,
        domain: params.domain,
        type: params.type,
      }),
    );

    dispatch(
      onboardingActions.wizard.register.request({
        domain: params.domain,
        origin: 'ai_agents',
      }),
    );

    history.push(`${routesLocale.sitesPage}${routesLocale.sitesCreate}`);
  }, [dispatch, history, routesLocale]);

  const handleRedirect = useCallback((params) => {
    if (params.internal === INTERNAL_REDIRECT_OPTIONS.BILLING || params.internal === INTERNAL_REDIRECT_OPTIONS.INVOICES) {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}`);
    }
  }, [history, routesLocale]);

  const handleOpenTicket = useCallback(() => {
    const openTicketRoute = `${routesLocale.help}${routesLocale.supportforms}`;
    history.push(openTicketRoute);
  }, [history, routesLocale]);

  const handleViewTickets = useCallback(() => {
    const viewTicketsRoute = `${routesLocale.help}`;
    history.push(viewTicketsRoute);
  }, [history, routesLocale]);

  const handleChatReady = useCallback(() => {
    setIsChatbotReady(true);
  }, []);

  const sendMessage = useCallback((message, ...args) => {
    if (!isActive) {
      console.log('Chatbot is not active');
      return;
    }
    window.Chatbot.sendMessage(message, ...args);
  }, [isActive]);

  const setProps = useCallback((props) => {
    sendMessage('props', props);
  }, [sendMessage]);

  const handleUpdateJwt = useCallback(() => {
    dispatch(authActions.tokens.jwt.update());
    setProps({
      auth: { jwt },
    });
  }, [dispatch, jwt, setProps]);


  const handleSetToast = useCallback(({
    message, type,
  }) => {
    dispatch(commonActions.notifications.set({
      label: message,
      type,
    }));
  }, [dispatch]);

  const handleUpdateHostAlerts = useCallback(({ alert }) => {
    dispatch(merlinActions.alerts.setAlerts({ alert }));
  }, [dispatch]);

  const handleGatorWithMultiAgent = useCallback(() => {
    dispatch(merlinActions.setWithMultiAgent());
  }, [dispatch]);

  const handleGatorWithN8n = useCallback(() => {
    dispatch(merlinActions.setWithN8n());
  }, [dispatch]);

  useEffect(() => {
    // Note: Need to set as active when script loaded (window.Chatbot is available), putting it on dependency array dont work since it is on global scope
    const checkChatbot = () => {
      if (newAiChatInterface && window && window.Chatbot) {
        // !IS_PROD && console.log('[PORTAL] - Chatbot Activated');
        setIsActive(true);
        return true;
      }
      return false;
    };

    if (checkChatbot()) return;

    const intervalId = setInterval(() => {
      if (checkChatbot()) {
        clearInterval(intervalId);
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAiChatInterface]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    on('state', handleStateChange);
    on('action:open-support', handleOpenSupport);
    on('action:migration-redirect', handleMigrationRedirect);
    on('action:redirect', handleRedirect);
    on('action:open-ticket', handleOpenTicket);
    on('action:view-tickets', handleViewTickets);
    on('ready', handleChatReady);
    on('update-jwt', handleUpdateJwt);
    on('action:set-toast', handleSetToast);
    on('action:update-host-alerts', handleUpdateHostAlerts);
    on('with-multi-agent', handleGatorWithMultiAgent);
    on('with-n8n', handleGatorWithN8n);

    return () => {
      off('state');
      off('action:open-support');
      off('action:migration-redirect');
      off('action:redirect');
      off('action:open-ticket');
      off('action:view-tickets');
      off('ready');
      off('update-jwt');
      off('actions:set-toast');
      off('action:update-host-alerts');
      off('with-multi-agent');
      off('with-n8n');
    };
  }, [
    isActive,
    on,
    off,
    handleOpenSupport,
    handleMigrationRedirect,
    handleRedirect,
    handleStateChange,
    handleOpenTicket,
    handleViewTickets,
    handleChatReady,
    handleUpdateJwt,
    handleSetToast,
    handleUpdateHostAlerts,
    handleGatorWithMultiAgent,
    handleGatorWithN8n,
  ]);

  const renderChat = ({ fullscreen = false } = {}) => {
    sendMessage('render-chat', { fullscreen });
  };

  const openAlerts = ({
    botMessage, alerts = [], alertsTypes = {}, fullScreen,
  }) => {
    sendMessage('open-alerts', {
      botMessage, alerts, alertsTypes, fullScreen,
    });
  };

  const updateChatAlerts = useCallback(({ alertsTypes, alerts }) => {
    sendMessage('update-chat-alerts', { alertsTypes, alerts });
  }, [sendMessage]);

  const sendTextMessage = ({ message, websocketType = 'n8n' }) => {
    sendMessage('text-message', { message, websocketType });
  };

  const sendAudioBlob = (blob) => {
    sendMessage('audio-blob', blob);
  };

  const setAgent = (agent) => {
    sendMessage('set-agent', agent);
  };

  const toggleMinimize = () => {
    sendMessage('toggle-minimize');
  };

  const toggleFullscreen = (fullscreen) => {
    sendMessage('toggle-fullscreen', fullscreen);
  };

  const setRoute = (route) => {
    sendMessage('set-route', route);
  };

  const showMultiAgentHome = (route) => {
    sendMessage('set-route', route);
  };

  const close = () => {
    sendMessage('show-multi-agent-home');
  };

  const init = (props) => {
    if (isInitialized) {
      return;
    }

    window.Chatbot.init(props);
    setIsInitialized(true);
  };

  const recalculateFullscreen = () => {
    window.Chatbot.recalculateFullscreen();
  };

  return (
    <ChatbotContext.Provider
      value={{
        renderChat,
        openAlerts,
        updateChatAlerts,
        sendTextMessage,
        sendAudioBlob,
        setAgent,
        toggleMinimize,
        init,
        isActive,
        isReady: isChatbotReady,
        recalculateFullscreen,
        toggleFullscreen,
        setRoute,
        close,
        showMultiAgentHome,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};
